import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";

// Placeholder components
import Profile from "./sub_comp/Profile";
import ManageEmployees from "./sub_comp/ManageEmployees";
import Reports from "./sub_comp/Reports";
import MyTasks from "./sub_comp/MyTasks";
import SubmitReport from "./sub_comp/SubmitReport";
import AssignTask from "./sub_comp/AssignTask";
import UpdatePassword from "./sub_comp/update_password";

import TransLoader from "../loader/TransLoader";
import CustomAlert from "../custom_alert/CustomAlert";
import GeneratePaySlip from "./sub_comp/generatepayslip";
import Downloadpayslip from "./sub_comp/Downloadpayslip";

export default function Dashboard() {
  const [config, setConfig] = useState(null);
  const [activeComponent, setActiveComponent] = useState("");
  const [loading, setLoading] = useState(false);
  const [renderedComponent, setRenderedComponent] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [user, setUser] = useState({ name: "", role: "", empId: "", sessionToken: "", des: "" });
  const [profile, setProfile] = useState(null);


  const navigate = useNavigate();
  const idleTimerRef = useRef(null);

  // Sidebar options — static
  const allOptions = {
    admin: [
      { label: "Reports", component: <Reports /> },
      { label: "Manage Employees", component: <ManageEmployees /> },
      { label: "Assign Task", component: <AssignTask /> },
      {label: "Generate Play Slip", component:<GeneratePaySlip/>}
    ],
    employee: [
      { label: "My Tasks", component: <MyTasks /> },
      { label: "Profile", component: <Profile /> },
      { label: "Submit Report", component: <SubmitReport /> },
      { label: "Download Payslip", component: <Downloadpayslip/> },
      { label: "Update Password", component: <UpdatePassword /> },
    ]
  };


  // Load config.json
  useEffect(() => {
    fetch("/config.json")
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(console.error);
  }, []);


  useEffect(() => {
    const sessionToken = sessionStorage.getItem("session_token");
    const role = sessionStorage.getItem("role").toLowerCase();
    const empId = sessionStorage.getItem("user_id");
    const name = sessionStorage.getItem("name");
    const des = sessionStorage.getItem("des");

    if (!sessionToken) {
      navigate("/"); // no session → redirect to login
    }
    // role = role.toLowerCase();
    // console.log("Role:", name);
    // console.log("Employee ID:", empId);
    // console.log("Session Token:", sessionToken);

    // Optionally store in state if you want to use reactively
    setUser({ name, role, empId, sessionToken, des });
  }, []);

  // Component switching with loader
  // ------------------------

  useEffect(() => {
    if (!user.role) return;

    const roleOptions = allOptions[user.role] || [];

    // Restore last active component if available
    const savedComponent = sessionStorage.getItem("activeComponent");
    const selected =
      roleOptions.find(opt => opt.label === activeComponent) ||
      roleOptions.find(opt => opt.label === savedComponent) ||
      roleOptions[0];

    setRenderedComponent(selected?.component || null);
  }, [activeComponent, user.role]);

  // ------------------------
  // Set default / restore activeComponent on role change
  // ------------------------
  useEffect(() => {
    if (!user.role) return;

    const roleOptions = allOptions[user.role] || [];
    const savedComponent = sessionStorage.getItem("activeComponent");

    if (savedComponent && roleOptions.some(opt => opt.label === savedComponent)) {
      setActiveComponent(savedComponent);
    } else {
      setActiveComponent(roleOptions[0]?.label || "");
    }
  }, [user.role]);

  // ------------------------
  // Persist activeComponent when it changes
  // ------------------------
  useEffect(() => {
    if (activeComponent) {
      sessionStorage.setItem("activeComponent", activeComponent);
    }
  }, [activeComponent]);



  // ------------------------
  // Logout Handler
  // ------------------------
  const handleLogout = useCallback(async (type = "manual", reason = "Logged out") => {
    setAlertMessage(reason);

    // Clear frontend storage
    sessionStorage.clear();
    localStorage.clear();

    // Call backend logout API
    if (config) {
      try {
        const response = await fetch(`${config.BASE_URL}${config.LOGOUT}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ logout_type: type }),
        });

        const data = await response.json();
        if (data.status === "success") {
          console.log("Logout successful");
        } else {
          console.warn("Logout failed:", data.message);
        }
      } catch (err) {
        console.error("Logout request failed:", err);
      }
    }

    // Redirect after short delay to allow alert display
    setTimeout(() => {
      navigate("/", { state: { logoutType: type, message: reason } });
    }, 500);
  }, [config, navigate]);

  const logout = () => handleLogout("manual", "You logged out");

  // ------------------------
  // Idle & Session Timeout
  // ------------------------
  const idleLimit = 10 * 60 * 1000; // 10 minutes

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      handleLogout("idle", "Session expired due to inactivity");
    }, idleLimit);
  }, [handleLogout]);


  const checkSession = useCallback(async () => {
    if (!config) return;

    const user_id = sessionStorage.getItem("user_id");
    const session_token = sessionStorage.getItem("session_token");

    if (!user_id || !session_token) {
      setAlertMessage("Session data missing. Please login again.");
      handleLogout("parallel", "Session data missing");
      return;
    }

    try {
      const response = await fetch(`${config.BASE_URL}${config.SESSION_VALID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_id,
          session_token,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success") {
        // Session is valid
        // setAlertMessage(`Session valid. Welcome ${result.user_id} (${result.role})`);
      } else {
        // Session invalid / expired / idle
        setAlertMessage(result.message || "Session invalid or expired");
        handleLogout("parallel", result.message || "Session invalid or expired");
      }
    } catch (err) {
      console.error("Session check failed:", err);
      setAlertMessage("Session check failed. Logging out.");
      handleLogout("parallel", "Session check failed");
    }
  }, [config, handleLogout]);


  useEffect(() => {
    resetIdleTimer();
    const events = ["mousemove", "keydown", "click"];
    events.forEach(evt => window.addEventListener(evt, resetIdleTimer));

    const interval = setInterval(checkSession, 60 * 1000); 

    return () => {
      events.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
      clearInterval(interval);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [resetIdleTimer, checkSession]);

  useEffect(() => {
    if (!config || !user.empId) return;

    const fetchProfile = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}${config.EMPLOYEE_DATA}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "RC_EMP_NEW"
          },
          body: JSON.stringify({ emp_code: user.empId }),
        });

        const data = await res.json();
        if (data.status === "success") {
          setProfile(data.employee);
          // console.log(data);
        } else {
          setProfile(null);
          console.warn(data.message);
        }
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        setProfile(null);
      }
    };

    fetchProfile();
  }, [config, user.empId]);


  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="company-logo-wrapper">
          <img
            src="/assets/TRADE_MARK_HD.png"
            alt="Company Logo"
            className="company-logo"
          />
        </div>

        <div className="sidebar-options">
          {(allOptions[user.role] || []).map(opt => (
            <button
              key={opt.label}
              className={`sidebar-btn ${activeComponent === opt.label ? "active" : ""}`}
              onClick={() => setActiveComponent(opt.label)}
            >
              {opt.label}
            </button>
          ))}
        </div>


        <button className="logout-btn" onClick={logout}>
          Logout
        </button>
      </div>

      {/* Main content */}
      <div className="main-content">
        <div className="top-bar">
          <h2 className="page-title">{activeComponent}</h2>
        </div>

        <div className="employee-info-top-right">
          {profile?.profile_image ? (
            <img
              src={
                typeof profile.profile_image === "string"
                  ? `data:image/jpeg;base64,${profile.profile_image}`
                  : URL.createObjectURL(profile.profile_image)
              }
              alt="Profile"
              className="profile-img"
            />
          ) : (
            <img
              src="/assets/TRADE_MARK_HD.png"   
              alt="Default Profile"
              className="profile-img"
            />
          )}
          <div className="employee-details">
            <span className="employee-name">
              {profile?.full_name || user?.name || "ADMIN"}
            </span>

            <span className="employee-id">
              {profile?.emp_code || ""}
            </span>

            <span className="employee-role">
              {profile?.role || user?.role || "Admin"}
            </span>

          </div>
        </div>


        {/* Dynamic content */}
        <div className="content-area">
          {loading ? <TransLoader /> : renderedComponent}
        </div>

        <CustomAlert
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      </div>
    </div >
  );
}
